import { parseZonedDateTime } from '@internationalized/date';
import { Edge } from '@xyflow/react';

import type { AutomationNode } from '../_components/editor/nodes/types';
import {
  AutomationRecord,
  AutomationStatus,
  TriggerType,
  AutomationSortOption,
} from '../types';

import { automationBaseFragment } from './fragments';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type AutomationsListParams = {
  searchTerm?: string;
  status?: AutomationStatus[];
  triggerType?: TriggerType[];
  sorting?: AutomationSortOption[];
  limit?: number;
  offset?: number;
};

export const automationsListQueryAtom = createQueryAtomFactory(
  'automations.list',
  (params: AutomationsListParams) => ({
    automations: {
      __args: {
        filter: {
          searchTerm: params.searchTerm || undefined,
          status: params.status,
          triggerType: params.triggerType,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: automationBaseFragment,
    },
  }),
  {
    select: ({
      automations,
    }): { total: number; automations: AutomationRecord[] } => {
      type AutomationGraphQLResult = {
        id: string;
        name: string;
        description?: string | null;
        status: AutomationStatus;
        triggerType: TriggerType;
        triggerConfig?: Record<string, unknown> | null;
        nodes?: unknown[] | null;
        edges?: unknown[] | null;
        workflowId?: string | null;
        workflowRunId?: string | null;
        organizationId?: string;
        timestamps: {
          createdAt: string;
          updatedAt: string;
        };
      };

      return {
        automations: automations.entries.map(
          (automation: AutomationGraphQLResult) => ({
            id: automation.id,
            name: automation.name,
            description: automation.description ?? null,
            status: automation.status as AutomationStatus,
            triggerType: automation.triggerType as TriggerType,
            triggerConfig: automation.triggerConfig ?? {},
            nodes: (automation.nodes ?? []) as AutomationNode[],
            edges: (automation.edges ?? []) as Edge[],
            workflowId: automation.workflowId ?? null,
            workflowRunId: automation.workflowRunId ?? null,
            organizationId: automation.organizationId ?? '',
            webhookEndpoint: null,
            timestamps: {
              createdAt: parseZonedDateTime(automation.timestamps.createdAt),
              updatedAt: parseZonedDateTime(automation.timestamps.updatedAt),
            },
          }),
        ),
        total: automations.page.total ?? 0,
      };
    },
  },
);
